/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';
import { Children as ChildrenPropTypes, element } from '@instructure/ui-prop-types';
import { TopNavBarActionItems } from '../TopNavBarActionItems';
import { TopNavBarBrand } from '../TopNavBarBrand';
import { TopNavBarMenuItems } from '../TopNavBarMenuItems';
import { TopNavBarUser } from '../TopNavBarUser';
import { topNavBarItemTooltipPropType } from '../TopNavBarItem/props';
import { TopNavBarBreadcrumb } from '../TopNavBarBreadcrumb';
const commonPropTypes = {
  renderBrand: ChildrenPropTypes.oneOf([TopNavBarBrand]),
  renderMenuItems: ChildrenPropTypes.oneOf([TopNavBarMenuItems]),
  renderActionItems: ChildrenPropTypes.oneOf([TopNavBarActionItems]),
  renderUser: ChildrenPropTypes.oneOf([TopNavBarUser]),
  renderBreadcrumb: ChildrenPropTypes.oneOf([TopNavBarBreadcrumb]),
  navLabel: PropTypes.string,
  elementRef: PropTypes.func
};
const desktopPropTypes = {
  hideActionsUserSeparator: PropTypes.bool
};
const smallViewportPropTypes = {
  dropdownMenuToggleButtonLabel: PropTypes.string.isRequired,
  dropdownMenuToggleButtonTooltip: topNavBarItemTooltipPropType,
  dropdownMenuLabel: PropTypes.string,
  alternativeTitle: PropTypes.node,
  renderInPlaceDialogConfig: PropTypes.shape({
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    closeButtonLabel: PropTypes.string.isRequired,
    content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    returnFocusElement: PropTypes.func,
    shouldContainFocus: PropTypes.bool,
    shouldCloseOnDocumentClick: PropTypes.bool,
    shouldCloseOnEscape: PropTypes.bool
  }),
  trayMountNode: PropTypes.oneOfType([element, PropTypes.func]),
  onDropdownMenuToggle: PropTypes.func,
  onDropdownMenuSelect: PropTypes.func,
  renderNavbarStartDangerousHack: PropTypes.node
};
const propTypes = {
  ...commonPropTypes,
  smallViewportConfig: PropTypes.shape(smallViewportPropTypes).isRequired
};
const commonAllowedProps = ['renderBrand', 'renderMenuItems', 'renderActionItems', 'renderUser', 'renderBreadcrumb', 'navLabel', 'elementRef'];
const desktopAllowedProps = ['hideActionsUserSeparator'];
const smallViewportAllowedProps = ['dropdownMenuToggleButtonLabel', 'dropdownMenuToggleButtonTooltip', 'dropdownMenuLabel', 'alternativeTitle', 'renderInPlaceDialogConfig', 'trayMountNode', 'onDropdownMenuToggle', 'onDropdownMenuSelect'];
const allowedProps = [...commonAllowedProps, 'smallViewportConfig'];
export { propTypes, allowedProps, commonPropTypes, commonAllowedProps, desktopPropTypes, smallViewportPropTypes, desktopAllowedProps, smallViewportAllowedProps };